import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  FileText,
  Plus,
  Loader2,
  Edit,
  Trash2,
  Upload,
  CheckCircle,
  GripVertical,
  X,
  Wand2,
  ArrowUp,
  ArrowDown,
  Columns,
  Search,
  Mail
} from "lucide-react";

const FIELD_TYPES = [
  { value: "text", label: "Texto" },
  { value: "number", label: "Número" },
  { value: "date", label: "Fecha" },
  { value: "textarea", label: "Texto largo" },
  { value: "select", label: "Lista de opciones" },
  { value: "checkbox", label: "Casilla de verificación" }
];

const COLUMN_WIDTHS = [
  { value: "full", label: "Ancho completo", icon: "▬▬▬▬" },
  { value: "half", label: "Mitad (1/2)", icon: "▬▬" },
  { value: "third", label: "Tercio (1/3)", icon: "▬" }
];

const SUGGESTED_FIELDS = [
  { name: "destinatario", label: "Nombre del Destinatario", field_type: "text" },
  { name: "empresa_destino", label: "Empresa Destino", field_type: "text" },
  { name: "telefono_destino", label: "Teléfono de Contacto", field_type: "text" },
  { name: "direccion_completa", label: "Dirección Completa", field_type: "textarea" },
  { name: "sector", label: "Sector/Zona", field_type: "text" },
  { name: "ciudad", label: "Ciudad", field_type: "text" },
  { name: "referencia", label: "Punto de Referencia", field_type: "text" },
  { name: "instrucciones_entrega", label: "Instrucciones de Entrega", field_type: "textarea" },
  { name: "horario_entrega", label: "Horario Preferido de Entrega", field_type: "select", options: ["Mañana (8am-12pm)", "Tarde (12pm-5pm)", "Cualquier hora"] },
  { name: "requiere_firma", label: "Requiere Firma de Recibido", field_type: "checkbox" },
  { name: "valor_declarado", label: "Valor Declarado", field_type: "number" },
  { name: "cantidad_piezas", label: "Cantidad de Piezas", field_type: "number" }
];

export default function PlantillasMensajeriaPage() {
  const [plantillas, setPlantillas] = useState([]);
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showSuggestedDialog, setShowSuggestedDialog] = useState(false);
  const [editingPlantilla, setEditingPlantilla] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [formData, setFormData] = useState({
    nombre: "",
    contenido: "",
    tipo_documento_id: "",
    fields: []
  });
  const [draggedIndex, setDraggedIndex] = useState(null);
  const fileInputRef = useRef(null);
  const { getAuthHeaders } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [plantillasRes, tiposRes] = await Promise.all([
        axios.get(`${API}/plantillas-mensajeria`, getAuthHeaders()),
        axios.get(`${API}/mensajeria/tipos-documento`, getAuthHeaders())
      ]);
      setPlantillas(plantillasRes.data);
      setTiposDocumento(tiposRes.data);
    } catch (err) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({
      nombre: "",
      contenido: "",
      tipo_documento_id: "",
      fields: []
    });
    setEditingPlantilla(null);
  };

  const openNewDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (plantilla) => {
    setEditingPlantilla(plantilla);
    setFormData({
      nombre: plantilla.nombre,
      contenido: plantilla.contenido || "",
      tipo_documento_id: plantilla.tipo_documento_id || "",
      fields: (plantilla.fields || []).map((f, idx) => ({
        ...f,
        column_width: f.column_width || "full",
        order: f.order ?? idx
      }))
    });
    setShowDialog(true);
  };

  const addField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      name: "",
      label: "",
      field_type: "text",
      required: false,
      options: [],
      column_width: "full",
      order: formData.fields.length
    };
    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
  };

  const addSuggestedField = (suggestedField) => {
    const newField = {
      id: `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: suggestedField.name,
      label: suggestedField.label,
      field_type: suggestedField.field_type,
      required: false,
      options: suggestedField.options || [],
      column_width: "full",
      order: formData.fields.length
    };
    setFormData(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
    toast.success(`Campo "${suggestedField.label}" agregado`);
  };

  const updateField = (index, key, value) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map((f, i) => 
        i === index ? { ...f, [key]: value } : f
      )
    }));
  };

  const removeField = (index) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index)
    }));
  };

  const moveField = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= formData.fields.length) return;
    
    const newFields = [...formData.fields];
    [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];
    newFields.forEach((f, idx) => f.order = idx);
    setFormData(prev => ({ ...prev, fields: newFields }));
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newFields = [...formData.fields];
    const draggedField = newFields[draggedIndex];
    newFields.splice(draggedIndex, 1);
    newFields.splice(index, 0, draggedField);
    newFields.forEach((f, idx) => f.order = idx);
    
    setFormData(prev => ({ ...prev, fields: newFields }));
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const res = await axios.post(
        `${API}/plantillas-mensajeria/generate-from-file`, 
        formDataUpload,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      const fieldsWithWidth = (res.data.fields || []).map((f, idx) => ({
        ...f,
        column_width: f.column_width || "full",
        order: idx
      }));

      setFormData(prev => ({
        ...prev,
        nombre: prev.nombre || res.data.suggested_name || "",
        contenido: prev.contenido || res.data.description || "",
        fields: [...prev.fields, ...fieldsWithWidth]
      }));
      
      toast.success(`Se extrajeron ${fieldsWithWidth.length} campos del documento`);
      setShowUploadDialog(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al procesar archivo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre.trim()) {
      toast.error("El nombre es requerido");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        nombre: formData.nombre,
        contenido: formData.contenido,
        tipo_documento_id: formData.tipo_documento_id || null,
        fields: formData.fields.map((f, idx) => ({
          ...f,
          order: idx
        }))
      };

      if (editingPlantilla) {
        await axios.put(`${API}/plantillas-mensajeria/${editingPlantilla.id}`, payload, getAuthHeaders());
        toast.success("Plantilla actualizada");
      } else {
        await axios.post(`${API}/plantillas-mensajeria`, payload, getAuthHeaders());
        toast.success("Plantilla creada");
      }
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (plantillaId) => {
    if (!confirm("¿Eliminar esta plantilla?")) return;
    try {
      await axios.delete(`${API}/plantillas-mensajeria/${plantillaId}`, getAuthHeaders());
      toast.success("Plantilla eliminada");
      fetchData();
    } catch (err) {
      toast.error("Error al eliminar");
    }
  };

  const filteredPlantillas = plantillas.filter(p =>
    p.nombre.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (p.contenido && p.contenido.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
              Plantillas de Mensajería
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Crea plantillas con campos personalizados para solicitudes de correspondencia
            </p>
          </div>
          <Button
            onClick={openNewDialog}
            className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Plantilla
          </Button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              placeholder="Buscar plantillas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Plantillas Grid */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Plantillas ({filteredPlantillas.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#0C314C]" />
              </div>
            ) : filteredPlantillas.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No hay plantillas creadas</p>
                <Button onClick={openNewDialog} variant="outline" className="mt-4">
                  Crear primera plantilla
                </Button>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filteredPlantillas.map(plantilla => (
                  <Card key={plantilla.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-slate-900 dark:text-white">
                          {plantilla.nombre}
                        </h3>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(plantilla)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(plantilla.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      {plantilla.tipo_documento_nombre && (
                        <Badge variant="outline" className="mb-2">
                          {plantilla.tipo_documento_nombre}
                        </Badge>
                      )}
                      <p className="text-sm text-slate-500 line-clamp-2 mb-2">
                        {plantilla.contenido || "Sin descripción"}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-slate-400">
                        <span>{(plantilla.fields || []).length} campos</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Main Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>
                {editingPlantilla ? "Editar Plantilla" : "Nueva Plantilla de Mensajería"}
              </DialogTitle>
              <DialogDescription>
                Configura los campos que aparecerán en el formulario de nueva solicitud
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
              <ScrollArea className="flex-1 pr-4" style={{ maxHeight: 'calc(90vh - 180px)' }}>
                <div className="space-y-6 pb-4 pr-2">
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="nombre">Nombre *</Label>
                      <Input
                        id="nombre"
                        value={formData.nombre}
                        onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                        placeholder="Ej: Correspondencia Legal"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="tipo_documento">Tipo de Documento</Label>
                      <Select
                        value={formData.tipo_documento_id || "none"}
                        onValueChange={(value) => setFormData({ ...formData, tipo_documento_id: value === "none" ? "" : value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sin tipo específico</SelectItem>
                          {tiposDocumento.map((tipo) => (
                            <SelectItem key={tipo.id} value={tipo.id}>
                              {tipo.nombre}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contenido">Descripción / Instrucciones</Label>
                    <Textarea
                      id="contenido"
                      value={formData.contenido}
                      onChange={(e) => setFormData({ ...formData, contenido: e.target.value })}
                      placeholder="Instrucciones para el usuario al llenar el formulario..."
                      rows={2}
                    />
                  </div>

                  {/* Fields Section */}
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-4">
                      <Label className="text-base font-semibold">Campos del Formulario</Label>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setShowSuggestedDialog(true)}
                        >
                          <Wand2 className="w-4 h-4 mr-1" />
                          Campos Sugeridos
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setShowUploadDialog(true)}
                        >
                          <Upload className="w-4 h-4 mr-1" />
                          Desde Archivo
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={addField}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Agregar Campo
                        </Button>
                      </div>
                    </div>

                    {formData.fields.length === 0 ? (
                      <div className="text-center py-8 text-slate-500 border-2 border-dashed rounded-lg">
                        <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No hay campos agregados</p>
                        <p className="text-sm">Usa los botones de arriba para agregar campos</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {formData.fields.map((field, index) => (
                          <div
                            key={field.id}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragEnd={handleDragEnd}
                            className={`border rounded-lg p-4 bg-white dark:bg-slate-800 ${
                              draggedIndex === index ? 'opacity-50' : ''
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className="cursor-move text-slate-400 hover:text-slate-600 pt-2">
                                <GripVertical className="w-5 h-5" />
                              </div>
                              
                              <div className="flex-1 grid grid-cols-4 gap-3">
                                <div className="space-y-1">
                                  <Label className="text-xs">Etiqueta</Label>
                                  <Input
                                    value={field.label}
                                    onChange={(e) => updateField(index, 'label', e.target.value)}
                                    placeholder="Etiqueta del campo"
                                    className="h-9"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Nombre interno</Label>
                                  <Input
                                    value={field.name}
                                    onChange={(e) => updateField(index, 'name', e.target.value.toLowerCase().replace(/\s/g, '_'))}
                                    placeholder="nombre_campo"
                                    className="h-9"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Tipo</Label>
                                  <Select
                                    value={field.field_type}
                                    onValueChange={(value) => updateField(index, 'field_type', value)}
                                  >
                                    <SelectTrigger className="h-9">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {FIELD_TYPES.map(type => (
                                        <SelectItem key={type.value} value={type.value}>
                                          {type.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Ancho</Label>
                                  <Select
                                    value={field.column_width || "full"}
                                    onValueChange={(value) => updateField(index, 'column_width', value)}
                                  >
                                    <SelectTrigger className="h-9">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {COLUMN_WIDTHS.map(w => (
                                        <SelectItem key={w.value} value={w.value}>
                                          {w.icon} {w.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>

                              <div className="flex items-center gap-2 pt-6">
                                <div className="flex items-center gap-1">
                                  <Switch
                                    checked={field.required}
                                    onCheckedChange={(checked) => updateField(index, 'required', checked)}
                                  />
                                  <Label className="text-xs">Req.</Label>
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => moveField(index, 'up')}
                                  disabled={index === 0}
                                >
                                  <ArrowUp className="w-4 h-4" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => moveField(index, 'down')}
                                  disabled={index === formData.fields.length - 1}
                                >
                                  <ArrowDown className="w-4 h-4" />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeField(index)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>

                            {field.field_type === 'select' && (
                              <div className="mt-3 ml-8">
                                <Label className="text-xs">Opciones (separadas por coma)</Label>
                                <Input
                                  value={(field.options || []).join(', ')}
                                  onChange={(e) => updateField(index, 'options', e.target.value.split(',').map(o => o.trim()).filter(Boolean))}
                                  placeholder="Opción 1, Opción 2, Opción 3"
                                  className="h-9 mt-1"
                                />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </ScrollArea>

              <DialogFooter className="border-t pt-4 mt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowDialog(false)}
                  disabled={saving}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      {editingPlantilla ? "Guardar Cambios" : "Crear Plantilla"}
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Upload Dialog */}
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Generar Campos desde Archivo
              </DialogTitle>
              <DialogDescription>
                Sube un documento (PDF, Word, Excel) y la IA extraerá los campos automáticamente
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.doc,.xlsx,.xls,.txt"
                onChange={handleFileUpload}
                disabled={uploading}
              />
              {uploading && (
                <div className="flex items-center gap-2 mt-4 text-blue-600">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Procesando documento con IA...</span>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUploadDialog(false)} disabled={uploading}>
                Cancelar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Suggested Fields Dialog */}
        <Dialog open={showSuggestedDialog} onOpenChange={setShowSuggestedDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wand2 className="w-5 h-5" />
                Campos Sugeridos
              </DialogTitle>
              <DialogDescription>
                Selecciona campos predefinidos para agregar a tu plantilla
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="max-h-96">
              <div className="space-y-2 pr-4">
                {SUGGESTED_FIELDS.map((field, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    <div>
                      <p className="font-medium">{field.label}</p>
                      <p className="text-sm text-slate-500">
                        Tipo: {FIELD_TYPES.find(t => t.value === field.field_type)?.label || field.field_type}
                        {field.options?.length > 0 && ` (${field.options.length} opciones)`}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addSuggestedField(field)}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Agregar
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSuggestedDialog(false)}>
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
